// @ts-check

/**
 * @file Renders primary timelog dashboard filters.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {memo} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Pressable, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"

import Card from "../../../components/card"
import Text from "@kaspernj/api-maker/build/utils/text"
import TextInput from "../../../components/text-input"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {"all" | "only_invoiced" | "only_uninvoiced"} TimelogDashboardStatusFilterValue */
/** @typedef {import("../../../../../shared/timelog-dashboard-types.js").TimelogDashboardFilterOption} TimelogDashboardFilterOption */

/**
 * @typedef {object} TimelogsDashboardFiltersProps
 * @property {string} dateRangeLabel Active date range label.
 * @property {() => unknown} onDateRangePress Date range button callback.
 * @property {() => unknown} onMoreFiltersPress More filters button callback.
 * @property {(optionId: string) => unknown} onProjectPress Project chip callback.
 * @property {(value: string) => unknown} onSearchChangeText Search input callback.
 * @property {() => unknown} onStatusFilterClearPress Status chip clear callback.
 * @property {(optionId: string) => unknown} onUserPress User chip callback.
 * @property {string[]} projectIds Active project identifiers.
 * @property {TimelogDashboardFilterOption[]} projectOptions Project filter options.
 * @property {string} searchQuery Active search query.
 * @property {string} [secondaryFilterLabel] Active secondary filter label.
 * @property {TimelogDashboardStatusFilterValue} statusFilterValue Active status filter.
 * @property {string[]} userIds Active user identifiers.
 * @property {TimelogDashboardFilterOption[]} userOptions User filter options.
 */

/** @typedef {Record<string, never>} TimelogsDashboardFiltersState */

/**
 * Displays primary filters for the timelog dashboard.
 * @augments {ShapeComponent<TimelogsDashboardFiltersProps, TimelogsDashboardFiltersState>}
 */
class TimelogsDashboardFilters extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = PropTypesExact({
    dateRangeLabel: PropTypes.string.isRequired,
    onDateRangePress: PropTypes.func.isRequired,
    onMoreFiltersPress: PropTypes.func.isRequired,
    onProjectPress: PropTypes.func.isRequired,
    onSearchChangeText: PropTypes.func.isRequired,
    onStatusFilterClearPress: PropTypes.func.isRequired,
    onUserPress: PropTypes.func.isRequired,
    projectIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    projectOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }).isRequired
    ).isRequired,
    searchQuery: PropTypes.string.isRequired,
    secondaryFilterLabel: PropTypes.string,
    statusFilterValue: PropTypes.oneOf([
      "all",
      "only_invoiced",
      "only_uninvoiced"
    ]).isRequired,
    userIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    userOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }).isRequired
    ).isRequired
  })

  /**
   * Subscribes filters to locale updates.
   * @returns {void} No return value.
   */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders dashboard filters.
   * @returns {ReactNode} Filter controls.
   */
  render() {
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const moreActive = this.p.statusFilterValue !== "all"
    return (
      <Card
        style={styles.card ||= {
          gap: 16
        }}
        testID="timelogsDashboardScreen/filters"
      >
        <View
          style={styles.headerRow ||= {
            gap: 12
          }}
          testID="timelogsDashboardScreen/filters/headerRow"
        >
          <Pressable
            onPress={this.p.onDateRangePress}
            style={styles[`dateButton-${themeName}`] ||= {
              alignItems: "center",
              backgroundColor: colors.infoBackground,
              borderColor: colors.info,
              borderRadius: 10,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 14,
              paddingVertical: 12
            }}
            testID="timelogsDashboardScreen/filters/dateButton"
          >
            <View
              style={styles.dateButtonIcon ||= {
                alignItems: "center",
                justifyContent: "center",
                marginRight: 8,
                width: 16
              }}
              testID="timelogsDashboardScreen/filters/dateButton/icon"
            >
              <FontAwesome6 color={colors.link} name="calendar-days" size={14} />
            </View>
            <Text
              style={styles[`dateButtonLabel-${themeName}`] ||= {
                color: colors.link,
                fontSize: 14,
                fontWeight: 700
              }}
              testID="timelogsDashboardScreen/filters/dateButton/label"
            >
              {this.p.dateRangeLabel}
            </Text>
          </Pressable>
          <Pressable
            onPress={this.p.onMoreFiltersPress}
            style={styles[`moreFiltersButton-${themeName}-${moreActive}`] ||= {
              alignItems: "center",
              backgroundColor: moreActive ? colors.infoBackground : colors.surface,
              borderColor: moreActive ? colors.info : colors.border,
              borderRadius: 10,
              borderWidth: 1,
              flexDirection: "row",
              paddingHorizontal: 14,
              paddingVertical: 12
            }}
            testID="timelogsDashboardScreen/filters/moreFiltersButton"
          >
            <View
              style={styles.moreFiltersButtonIcon ||= {
                marginRight: 8,
                width: 16
              }}
              testID="timelogsDashboardScreen/filters/moreFiltersButton/icon"
            >
              <FontAwesome6 color={moreActive ? colors.link : colors.text} name="sliders" size={14} />
            </View>
            <Text
              style={styles[`moreFiltersButtonLabel-${themeName}-${moreActive}`] ||= {
                color: moreActive ? colors.link : colors.text,
                fontSize: 14,
                fontWeight: 700
              }}
              testID="timelogsDashboardScreen/filters/moreFiltersButton/label"
            >
              {this.p.secondaryFilterLabel || _("More filters")}
            </Text>
          </Pressable>
          <View
            style={styles.searchField ||= {
              gap: 6
            }}
            testID="timelogsDashboardScreen/filters/searchField"
          >
            <Text
              style={styles[`filterLabel-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase"
              }}
              testID="timelogsDashboardScreen/filters/searchLabel"
            >
              {_("Search")}
            </Text>
            <TextInput
              onChangeText={this.p.onSearchChangeText}
              placeholder={_("Search by comment...")}
              style={styles.searchInput ||= {borderRadius: 10}}
              testID="timelogsDashboardScreen/filters/searchInput"
              value={this.p.searchQuery}
            />
          </View>
        </View>
        {this.p.secondaryFilterLabel &&
          <View
            style={styles.secondaryFiltersSection ||= {
              gap: 8
            }}
            testID="timelogsDashboardScreen/filters/secondaryFilters"
          >
            <Text
              style={styles[`filterLabel-${themeName}`]}
              testID="timelogsDashboardScreen/filters/secondaryFilters/label"
            >
              {_("More filters")}
            </Text>
            <Pressable
              onPress={this.p.onStatusFilterClearPress}
              style={styles[`secondaryFilterChip-${themeName}`] ||= {
                alignItems: "center",
                alignSelf: "flex-start",
                backgroundColor: colors.infoBackground,
                borderColor: colors.info,
                borderRadius: 999,
                borderWidth: 1,
                flexDirection: "row",
                paddingHorizontal: 12,
                paddingVertical: 8
              }}
              testID="timelogsDashboardScreen/filters/secondaryFilters/statusChip"
            >
              <View
                style={styles.secondaryFilterChipIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID="timelogsDashboardScreen/filters/secondaryFilters/statusChip/icon"
              >
                <FontAwesome6 color={colors.link} name="xmark" size={13} />
              </View>
              <Text
                style={styles[`secondaryFilterChipLabel-${themeName}`] ||= {
                  color: colors.link,
                  fontSize: 13,
                  fontWeight: 600
                }}
                testID="timelogsDashboardScreen/filters/secondaryFilters/statusChip/label"
              >
                {this.p.secondaryFilterLabel}
              </Text>
            </Pressable>
          </View>
        }
        <FilterChipSectionComponent
          activeIds={this.p.projectIds}
          onPress={this.p.onProjectPress}
          options={this.p.projectOptions}
          sectionTestID="timelogsDashboardScreen/filters/projects"
          title={_("Projects")}
        />
        <FilterChipSectionComponent
          activeIds={this.p.userIds}
          onPress={this.p.onUserPress}
          options={this.p.userOptions}
          sectionTestID="timelogsDashboardScreen/filters/users"
          title={_("Users")}
        />
      </Card>
    )
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardFiltersProps>} */
const TimelogsDashboardFiltersComponent = memo(shapeComponent(TimelogsDashboardFilters))

export default TimelogsDashboardFiltersComponent

/**
 * @typedef {object} FilterChipSectionProps
 * @property {string[]} activeIds Active option identifiers.
 * @property {(optionId: string) => unknown} onPress Option press callback.
 * @property {TimelogDashboardFilterOption[]} options Filter options to render.
 * @property {string} sectionTestID Base test id for the section.
 * @property {string} title Section heading.
 */

/** @typedef {Record<string, never>} FilterChipSectionState */

/**
 * Displays one group of selectable filter chips.
 * @augments {ShapeComponent<FilterChipSectionProps, FilterChipSectionState>}
 */
class FilterChipSection extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  pressHandlers = /** @type {Record<string, () => void>} */ {}
  static propTypes = PropTypesExact({
    activeIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    onPress: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }).isRequired
    ).isRequired,
    sectionTestID: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the chip section.
   * @returns {ReactNode} Filter chip section content.
   */
  render() {
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles.section ||= {
          gap: 8
        }}
        testID={this.p.sectionTestID}
      >
        <Text
          style={styles[`filterLabel-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase"
          }}
          testID={`${this.p.sectionTestID}/label`}
        >
          {this.p.title}
        </Text>
        <View
          style={styles.chipsRow ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8
          }}
          testID={`${this.p.sectionTestID}/chips`}
        >
          {this.p.options.map((option) => {
            const active = this.p.activeIds.includes(option.id)
            return (
              <Pressable
                key={option.id}
                onPress={
                  this.pressHandlers[option.id] ||= () =>
                    this.p.onPress(option.id)
                }
                style={styles[`chip-${themeName}-${active}`] ||= {
                  alignItems: "center",
                  backgroundColor: active ? colors.primary : colors.surfaceMuted,
                  borderColor: active ? colors.primary : colors.border,
                  borderRadius: 999,
                  borderWidth: 1,
                  flexDirection: "row",
                  paddingHorizontal: 12,
                  paddingVertical: 8
                }}
                testID={`${this.p.sectionTestID}/chip-${option.id}`}
              >
                <View
                  style={styles.sectionChipIcon ||= {
                    marginRight: 8,
                    width: 14
                  }}
                  testID={`${this.p.sectionTestID}/chip-${option.id}/icon`}
                >
                  <FontAwesome6 color={active ? colors.onPrimary : colors.text} name="filter" size={13} />
                </View>
                <Text
                  style={styles[`chipText-${themeName}-${active}`] ||= {
                    color: active ? colors.onPrimary : colors.text,
                    fontSize: 13,
                    fontWeight: 600
                  }}
                  testID={`${this.p.sectionTestID}/chip-${option.id}/label`}
                >
                  {option.name}
                </Text>
              </Pressable>
            )
          })}
        </View>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<FilterChipSectionProps>} */
const FilterChipSectionComponent = memo(shapeComponent(FilterChipSection))
